import type { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { format } from 'app/shared/utils/string.util';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { mergeCredentialOption } from '../utls/util';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { ConnectionAuthKind } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { compact } from 'lodash';

export const azureDataLakeStoreCosmosStructuredStreamMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azureDataLakeStoreCosmosStructuredStream.linkedServiceType,
        displayName: stringResources.AzureDataLakeStoreCosmosStructuredStream_DisplayText,
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i-100x100-AzureDataLake'
        },
        notSupportCopySink: true,
        isFirstClass: true,
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.azureDataLakeStoreCosmosStructuredStream.copySourceType],
        category: ['Azure'],
        supportDatasetV2: true,
        supportGetMetadataActivity: true,
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'AzureDataLakeStoreCosmosStructuredStream' as ResourceKind
        },
        supportedAuthKindsInTrident: compact([ConnectionAuthKind.ServicePrincipal,
            ConnectionAuthKind.WorkspaceIdentity
        ]),
        supportOPDG: GlobalVariables.enableOPDG,
        controls: [
            {
                type: 'radio-switch',
                nested: 1,
                label: stringResources.AzureDataLakeStoreCosmosStructuredStream_AccountName_SelectionMethod,
                description: stringResources.AzureDataLakeStore_AccountName_SelectionMethod_Description,
                options: azureResourceSelectMethodOptions,
                isLabelVertical: true,
                defaultValue: '{accountName != null ? "manual" : "azure"}',
                cases: [
                    {
                        caseValue: 'azure',
                        children: [
                            {
                                type: 'azure-data-lake-store-account-select',
                                bind: {
                                    accountName: 'accountName',
                                    subscriptionId: 'subscriptionId',
                                    resourceGroupName: 'resourceGroupName'
                                },
                                validators: {
                                    accountName: [
                                        { type: 'required', errorMessage: '' }
                                    ]
                                }
                            }
                        ]
                    },
                    {
                        caseValue: 'manual',
                        children: [
                            {
                                type: 'textbox',
                                bind: 'accountName',
                                label: stringResources.ModelName_AccountName,
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' },
                                    {
                                        type: 'pattern',
                                        pattern: '^[a-z0-9\-]{3,}$',
                                        errorMessage: stringResources.Validation_StorageAccountNameGen1
                                    }
                                ],
                                isLabelVertical: true
                            },
                            {
                                type: 'textbox',
                                bind: 'subscriptionId',
                                label: stringResources.SubscriptionId,
                                isLabelVertical: true
                            },
                            {
                                type: 'textbox',
                                bind: 'resourceGroupName',
                                label: stringResources.ModelName_ResourceGroupName,
                                isLabelVertical: true
                            }
                        ]
                    }
                ]
            },
            {
                type: 'tenant-textbox',
                bind: 'tenant',
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
                isLabelVertical: true,
                label: stringResources.ModelName_Tenant,
                required: true
            },
            {
                type: 'select-switch',
                bind: 'authenticationType',
                label: stringResources.ModelName_AuthenticationType,
                options: mergeCredentialOption([
                    {
                        label: stringResources.AuthenticationType_ManagedServiceIdentity,
                        value: AuthenticationType.ManagedServiceIdentity
                    },
                    {
                        label: stringResources.AuthenticationType_ServicePrincipal,
                        value: AuthenticationType.ServicePrincipal
                    }
                ]),
                required: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
                isLabelVertical: true,
                defaultValue: AuthenticationType.ManagedServiceIdentity,
                cases: [
                    {
                        caseValue: AuthenticationType.ServicePrincipal,
                        children: [
                            {
                                type: 'textbox',
                                bind: 'servicePrincipalId',
                                label: stringResources.ModelName_ServicePrincipalID,
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ],
                                isLabelVertical: true,
                                description: '<a target="_blank" href="https://learn.microsoft.com/azure/data-factory/connector-azure-data-lake-store?tabs=data-factory#use-service-principal-authentication">'
                                    + stringResources.LearnMore + '</a>',
                                escapeDescription: false
                            },
                            {
                                type: 'dynamic-akv-switcher',
                                bind: 'servicePrincipalKey',
                                label: stringResources.ModelName_ServicePrincipalKey,
                                child: {
                                    type: 'password',
                                    label: stringResources.ModelName_ServicePrincipalKey,
                                    required: true,
                                    isLabelVertical: true
                                },
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ],
                                description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                    format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureDataLakeStoreCosmosStructuredStream_DisplayText),
                                    stringResources.ModelName_ServicePrincipalKey)
                            }
                        ]
                    },
                    {
                        caseValue: AuthenticationType.ManagedServiceIdentity,
                        children: [
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureDataLakeStore_DisplayText,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-data-lake-store?tabs=data-factory#managed-identity'
                            }
                        ]
                    },
                    {
                        caseValue: AuthenticationType.Credential,
                        children: [
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        ]
                    }
                ]
            }
        ]
    };

    return metadata;
};
